import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AgGridService, UserStorageService } from '@approot/services';
import DropDownOptions from '@approot/modals/trade-capture/schedule-reports/report-dropdown-options.json';
import { clone, SubscriptionSink } from '@approot/utilities';
import {
  AgGridEvent,
  ColDef,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import { isEqual } from 'lodash';
import {
  auditTime,
  debounceTime,
  distinctUntilChanged,
  take
} from 'rxjs/operators';
import { applyColumnState } from './risks-blotter.funcs';
import { RisksBlotterService } from './risks-blotter.service';
import { RisksBlotterState, RunningState } from './risks-blotter.types';
import {
  RiskBlotterGridStateKey,
  RiskBlotterTimeZoneStateKey
} from '@approot/models';
import { sorter } from '../shared/utils';

@Component({
  selector: 'app-risks-blotter',
  templateUrl: './risks-blotter.component.html',
  styleUrls: ['./risks-blotter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RisksBlotterComponent implements OnInit, OnDestroy {
  public sideBarDef: SideBarDef;
  public state: RisksBlotterState = {};
  public entities: string[] = [];
  public runningState: RunningState = RunningState.Idle;
  public RunningState = RunningState;
  public readonly defaultColDef: ColDef = {
    sortable: true,
    enableRowGroup: true,
    resizable: true
  };
  public headers: any[] = [];
  public rowData: any[] = [];
  public readonly overlayNoRowsTemplate =
    '<span style="padding: 10px; border: 2px solid #444;">No risks loaded</span>';
  public form: FormGroup = new FormGroup({
    entity: new FormControl<string>(null),
    timezone: new FormControl<string>('Europe/London'),
    date: new FormControl<string>(null),
    time: new FormControl<string>(null)
  });
  public timezones: string[] = DropDownOptions.timezones;

  private subsink = new SubscriptionSink();
  private agGridService = inject(AgGridService);
  private changeRef = inject(ChangeDetectorRef);
  private risksBlotterService = inject(RisksBlotterService);
  private userStorageService = inject(UserStorageService);

  public constructor() {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subsink.sink = this.form.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged((a, b) => isEqual(a, b))
      )
      .subscribe(v => this.risksBlotterService.update({ selectedEntry: v }));

    this.subsink.sink = this.form
      .get('timezone')
      .valueChanges.pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe(tz =>
        this.userStorageService.setItem(RiskBlotterTimeZoneStateKey, tz)
      );

    const tz =
      this.userStorageService.getItem(RiskBlotterTimeZoneStateKey) ||
      'Europe/London';
    this.form.get('timezone').setValue(tz, { emitEvent: false });
  }

  public ngOnInit(): void {
    // initial state.  Although we resubscribe to stateChanged in onGridReady, we
    // get it immediately (i.e., no `auditTime()`) so that the form is populated with
    // the correct entity, otherwise there's an annoying delay.
    this.subsink.sink = this.risksBlotterService.stateChanged
      .pipe(take(1))
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public onGridReady(params: GridReadyEvent) {
    this.subsink.sink = this.risksBlotterService.stateChanged
      .pipe(
        auditTime(250) // Don't update too often
      )
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });

    const columnState = this.userStorageService.getItem(
      RiskBlotterGridStateKey
    );
    // sometimes we get a string of 'undefined' from local storage so ignore that
    if (columnState && columnState !== 'undefined') {
      applyColumnState(params.columnApi, columnState);
    }
  }

  public canRunReport(): boolean {
    const form = this.form.getRawValue();
    const validDate = /^\d\d\d\d-\d\d-\d\d$/.test(form['date']);
    const validTime = /^\d\d:\d\d$/.test(form['time']);
    if (form['date'] && !validDate) {
      return false;
    }
    if (form['time'] && !validTime) {
      return false;
    }
    if (validTime && !validDate) {
      return false;
    }
    if (form['entity'] == null) {
      return false;
    }
    return true;
  }

  public runReport() {
    const form = this.form.getRawValue();
    if (!this.canRunReport()) {
      return;
    }

    this.risksBlotterService.runReport(form);
  }

  public cancelReport() {
    this.risksBlotterService.cancelReport();
  }

  public gridStateChanged(event: AgGridEvent): void {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(RiskBlotterGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(RiskBlotterGridStateKey);
    }
  }

  private updateOnStateChange(state: RisksBlotterState) {
    this.state = state;
    // We don't want to update the grid if the data unchanged
    if (this.rowData.length !== state.rows.length) {
      this.rowData = state.rows;
    }
    if (this.entities.length !== state.entities.length) {
      this.entities = clone(state).entities.sort(sorter);
    }
    if (this.headers.length !== state.headers.length) {
      this.headers = state.headers;
    }
    this.form
      .get('entity')
      .setValue(state.selectedEntry?.entity, { emitEvent: false });
    this.changeRef.markForCheck();
  }
}
